/**
 * Gerador principal de LaTeX
 * Orquestra a geração do documento completo
 */

import { DadosApreciacao, Risco } from '../core/latex-types';
import { obterPreambulo } from '../static/latex-preamble';
import { gerarCapaLatex } from './latex-capa';
import { gerarSumarioLatex } from './latex-sumario';
import { gerarSecaoMetodologia } from './latex-metodologia';
import { IMAGENS_ESTATICAS } from '../static/latex-static-assets';
import { gerarSecaoBNormasAbreviacoes } from './latex-informacoes-documento';
import { escapeLatex } from '../core/latex-utils';
import { gerarSecaoCCadastroMaquina } from '../sections/latex-secao-c-cadastro-maquina';
import { gerarSecaoDApreciacoesRiscos } from '../sections/latex-secao-d-apreciacoes-riscos';
import { gerarLatexPraxis } from './latex-generator-praxis';
import { gerarLatexAr } from './latex-generator-ar';

/**
 * Gera o código LaTeX completo do documento
 */
export function gerarLatex(dados: DadosApreciacao, riscos: Risco[]): string {
  const clienteTipo = dados.projeto?.clienteTipo || dados.clienteTipo || 'arcelor';
  if (clienteTipo === 'praxis') {
    return gerarLatexPraxis(dados, riscos);
  }
  if (clienteTipo === 'ar') {
    return gerarLatexAr(dados, riscos);
  }
  return gerarLatexArcelor(dados, riscos);
}

/**
 * Gera o código LaTeX completo do documento Arcelor (fluxo legado preservado)
 */
function gerarLatexArcelor(dados: DadosApreciacao, riscos: Risco[]): string {
  let latex = obterPreambulo(dados.numeroDocumento, dados.revisao, dados.dataEmissao);
  latex += `\\begin{document}

`;
  latex += gerarCapaLatex(dados, riscos);
  latex += gerarSumarioLatex(dados, riscos);
  latex += gerarSecaoMetodologia();
  latex += gerarConteudoCompleto(dados, riscos);
  latex += `\\end{document}`;
  return latex;
}

/**
 * Gera o conteúdo completo do documento (após a capa)
 */
function gerarConteudoCompleto(dados: DadosApreciacao, riscos: Risco[]): string {
  let latex = '';

  // Seção B - Normas e Abreviações
  latex += gerarSecaoBNormasAbreviacoes();

  // Seção C - Cadastro da máquina (inclui todas as subseções, incluindo lista de partes)
  if (dados.maquina) {
    latex += gerarSecaoCCadastroMaquina(dados, riscos || []);
  }

  // Seção D - Apreciações de riscos
  if (riscos && riscos.length > 0) {
    // Priorizar o Número do Emitente para a numeração dos perigos (conforme solicitado pelo usuário)
    const referenciaPerigo = dados.numeroEmitente || dados.ativo?.refSismanaNovo || dados.ativo?.refSismanaAntigo || '';
    latex += gerarSecaoDApreciacoesRiscos(riscos, referenciaPerigo);
  }

  // Seção E - Conclusão (era H) com assinaturas na mesma página
  latex += gerarSecaoEConclusaoComAssinaturas(dados);

  // Seção F - Exclusão de Responsabilidade (era I) - REMOVIDA TEMPORARIAMENTE
  // latex += gerarSecaoFExclusaoResponsabilidade();

  return latex;
}

/**
 * Obtém o nome completo do usuário (executante ou responsável técnico)
 */
function obterNomeUsuario(usuario: any, externo: any): string {
  if (usuario?.nome) return usuario.nome;
  if (externo?.nome) return externo.nome;
  return '';
}

/**
 * Obtém o cargo/função do usuário.
 *
 * Para dados externos prioriza `funcao` (campo editável no formulário "Função"),
 * caindo para `cargo` como fallback. Sem essa ordem, editar "Função" na tela
 * ficava sem efeito quando o perfil já tinha um `cargo` pré-definido (perfis
 * Lauan/Luciano).
 */
function obterCargoUsuario(usuario: any, externo: any): string {
  if (externo?.funcao) return externo.funcao;
  if (externo?.cargo) return externo.cargo;
  if (usuario?.cargo) return usuario.cargo;
  return '';
}

/**
 * Obtém o CREA do usuário
 */
function obterCreaUsuario(usuario: any, externo: any): string {
  if (usuario?.crea) return usuario.crea;
  if (externo?.crea) return externo.crea;
  return '';
}

/**
 * Obtém a URL da assinatura (imagem ou digital)
 */
function obterAssinaturaUrl(usuario: any, externo: any): string | null {
  if (usuario?.assinaturaUrl) return usuario.assinaturaUrl;
  if (usuario?.assinaturaDigital) return usuario.assinaturaDigital;
  if (externo?.assinaturaUrl) return externo.assinaturaUrl;
  if (externo?.assinaturaDigital) return externo.assinaturaDigital;
  return null;
}

/**
 * Gera a Seção E - Conclusão (era H) com assinaturas na mesma página
 */
function gerarSecaoEConclusaoComAssinaturas(dados: DadosApreciacao): string {
  // Formatar data (usar dataAprovacao se disponível, depois dataEmissao, senão data atual)
  const meses = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];

  const obterDataConclusao = (): string => {
    const fonte = (dados.dataAprovacao as any) || (dados.dataEmissao as any) || new Date();
    // Se vier como string ISO (YYYY-MM-DD), evitar problemas de timezone
    if (typeof fonte === 'string') {
      const str = fonte.substring(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [anoStr, mesStr, diaStr] = str.split('-');
        const ano = Number(anoStr);
        const mesIndex = Number(mesStr) - 1;
        const dia = Number(diaStr);
        const cidade = 'Boituva'; // Cidade padrão
        const mesNome = meses[mesIndex] || meses[0];
        return `${cidade}, ${dia} de ${mesNome} de ${ano}`;
      }
    }

    const data = fonte instanceof Date ? fonte : new Date(fonte);
    const dia = data.getDate();
    const mesNome = meses[data.getMonth()];
    const ano = data.getFullYear();
    const cidade = 'Boituva'; // Cidade padrão
    return `${cidade}, ${dia} de ${mesNome} de ${ano}`;
  };

  const dataFormatada = obterDataConclusao();

  // Obter dados do executante
  const executanteNome = obterNomeUsuario(dados.executante, dados.executanteExterno) || 'Lauan Rodrigues de Mattos Costa';
  const executanteCargo = obterCargoUsuario(dados.executante, dados.executanteExterno) || 'Técnico de Planejamento';
  const executanteCrea = obterCreaUsuario(dados.executante, dados.executanteExterno);
  const executanteAssinatura = obterAssinaturaUrl(dados.executante, dados.executanteExterno);

  // Obter dados do responsável técnico
  const responsavelNome = obterNomeUsuario(dados.responsavelTecnico, dados.responsavelTecnicoExterno) || 'Luciano Pereira Siqueira';
  const responsavelCargo = obterCargoUsuario(dados.responsavelTecnico, dados.responsavelTecnicoExterno) || 'Engenheiro de Aplicação';
  const responsavelCrea = obterCreaUsuario(dados.responsavelTecnico, dados.responsavelTecnicoExterno) || 'ES-033232/D';
  const responsavelAssinatura = obterAssinaturaUrl(dados.responsavelTecnico, dados.responsavelTecnicoExterno);

  // Retorna o caminho/URL para \includegraphics: base64 (backend processa), arquivo estático ou fallback
  const caminhoAssinatura = (url: string | null, fallback: string): string => {
    if (!url) return fallback;
    // Incluir base64 no LaTeX para o backend processar e substituir por arquivo
    if (url.startsWith('data:image')) return url;
    // Arquivo estático (ex.: Lauan.jpg, Luciano.jpg) ou outro caminho
    return url;
  };

  const executantePath = caminhoAssinatura(executanteAssinatura, IMAGENS_ESTATICAS.ASSINATURA_EXECUTANTE);
  const executanteAssinaturaLatex = `\\includegraphics[height=2cm,width=4cm,keepaspectratio]{${executantePath}}`;

  const responsavelPath = caminhoAssinatura(responsavelAssinatura, IMAGENS_ESTATICAS.ASSINATURA_RESPONSAVEL);
  const responsavelAssinaturaLatex = `\\includegraphics[height=1.5cm,width=4cm,keepaspectratio]{${responsavelPath}}`;

  // Converte texto com quebras de linha para LaTeX (cada linha escapada, unida por \\)
  const funcaoMultilinhaLatex = (texto: string): string => {
    if (!texto || !texto.trim()) return '';
    return texto.split(/\r?\n/).map((l) => escapeLatex(l.trim())).filter(Boolean).join('\\\\\n');
  };

  // Construir informações do executante
  let executanteInfo = `Executante: ${escapeLatex(executanteNome)}`;
  if (executanteCargo) {
    const funcaoLatex = funcaoMultilinhaLatex(executanteCargo);
    if (funcaoLatex) executanteInfo += `\\\\\nFunção: ${funcaoLatex}`;
  }
  if (executanteCrea) {
    executanteInfo += `\\\\\nCREA: ${escapeLatex(executanteCrea)}`;
  }

  // Construir informações do responsável técnico
  let responsavelInfo = `Responsável Técnico: ${escapeLatex(responsavelNome)}`;
  if (responsavelNome === 'Luciano Pereira Siqueira' || responsavelNome.includes('Luciano Pereira Siqueira')) {
    responsavelInfo += `\\\\\nEngº: Mecatrônico (CREA, ES-033232/D)`;
    responsavelInfo += `\\\\\nEngº: Segurança do Trabalho (CREA, ES-033232/D)`;
    responsavelInfo += `\\\\\nFS Engineer (TÜV Rheinland,\\# 25519/ 22,SIS)`;
    responsavelInfo += `\\\\\nMCEexpert® (TÜV Rheinland,\\# 0000068649)`;
  } else {
    if (responsavelCargo) {
      const funcaoLatex = funcaoMultilinhaLatex(responsavelCargo);
      if (funcaoLatex) responsavelInfo += `\\\\\nFunção: ${funcaoLatex}`;
    }
    if (responsavelCrea) {
      responsavelInfo += `\\\\\nCREA: ${escapeLatex(responsavelCrea)}`;
    }
  }

  return `
\\newpage 

\\noindent\\section*{Seção E - Conclusão}
\\addcontentsline{toc}{section}{E. Conclusão}
\\label{sec:conclusao}

\\setlength\\parindent{24pt}
\\par
Para a máquina apresentada neste documento de apreciação de riscos, cada parte ou atividade apresentada na Seção D - Apreciações de riscos deverá estar com o estado de "em conformidade" na cor verde. Caso estejam como "não conformidade", na cor vermelha, o proprietário da máquina deverá providenciar as adequações sugeridas na respectiva parte ou atividade a fim de atender os requisitos normativos.
\\\\

Objetivando reduzir os riscos, foram considerados os aspectos das normas tipo C, quando aplicáveis, a ABNT NBR ISO 12100 e a metodologia HRN para a quantificação do grau dos riscos. Conforme indica o "estado da técnica", foram aplicados os meios técnicos mais eficazes possíveis para a redução de riscos a um nível aceitável, permitindo assim declarar que, após todas as partes e atividades da Seção D estejam em conformidade, a máquina esteja de acordo com os requisitos da NR 12 contidos neste relatório.
\\\\
\\par
Importante salientar que a Apreciação de Riscos apresentada contempla os perigos identificados na máquina por meio de exame visual conforme a ABNT NBR ISO 12100, abrangendo, quando aplicáveis, os perigos mecânicos, elétricos, térmicos e demais perigos pertinentes às partes e atividades avaliadas, em atendimento aos requisitos da NR-12. Os perigos que demandam avaliação específica encontram-se relacionados nas delimitações do relatório (Seção C.7).
\\\\
\\par
Os riscos físicos, químicos e biológicos associados a esta máquina encontram-se devidamente levantados e caracterizados no LTCAT (Laudo Técnico das Condições Ambientais de Trabalho) e gerenciados no PGR (Programa de Gerenciamento de Riscos) da Segurança e Saúde do Trabalho da ArcelorMittal Tubarão, nos termos do art. 1º da Portaria SEPRT nº 6.730/2020 e do \\S~3º do art. 1º da NR-01.
\\\\
\\par
Analogamente, o risco ergonômico desta máquina está estabelecido na Análise Ergonômica do Trabalho (AET), conforme os parâmetros da NR-17 e suas atualizações, sendo igualmente gerenciado pelo PGR da unidade, assegurando a abordagem integrada e multidisciplinar dos fatores de risco ocupacionais.
\\\\
\\par
Este documento abrange a vida útil da máquina avaliada, a partir da data de fabricação, conforme padrão do Instituto Brasileiro de Avaliações e Perícias de Engenharia de São Paulo (IBAPE/SP), caso não exista esta informação descrita pelo fabricante.

\\vspace{0.5cm}

\\begin{flushright}
${escapeLatex(dataFormatada)}
\\end{flushright}

\\vspace{1cm}

% Assinatura do Executante
\\begin{flushleft}
${executanteAssinaturaLatex}\\\\
\\rule{10cm}{0.4pt}\\\\
\\vspace{0.3cm}
${executanteInfo}
\\end{flushleft}

\\vspace{3cm}

% Assinatura do Responsável Técnico
\\begin{flushleft}
${responsavelAssinaturaLatex}\\\\
\\rule{10cm}{0.4pt}\\\\
\\vspace{0.3cm}
${responsavelInfo}
\\end{flushleft}

`;
}


